import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  submitVersion as submitVersionApi,
  decideVersion as decideVersionApi,
  type IDocument,
} from '@apis/documents';

export type ApprovalOperation = 'submit' | 'decide';

export function versionKey(documentId: string, seq: number): string {
  return `${documentId}:${seq}`;
}

export function useVersionApproval(
  siteId: string | undefined,
  onError?: (op: ApprovalOperation, error: unknown) => void,
) {
  const queryClient = useQueryClient();
  const [busyKey, setBusyKey] = useState<string | null>(null);

  async function run(
    op: ApprovalOperation,
    doc: IDocument,
    seq: number,
    call: (siteId: string) => Promise<void>,
  ): Promise<boolean> {
    if (!siteId || !doc.documentId) return false;
    setBusyKey(versionKey(doc.documentId, seq));
    try {
      await call(siteId);
      // The row's version, status and history all change — refetch rather than
      // guess, since a decision can also make the document visible to others.
      // Inside the busy window on purpose: the call and the refetch are two
      // round trips, and the drawer shows the OLD state for both of them.
      await queryClient.invalidateQueries({ queryKey: ['documents', 'files', siteId, doc.folderId] });
      return true;
    } catch (err) {
      onError?.(op, err);
      return false;
    } finally {
      setBusyKey(null);
    }
  }

  return {
    busyKey,
    submit: (doc: IDocument, seq: number) =>
      run('submit', doc, seq, (site) => submitVersionApi(site, doc.documentId!, seq)),
    decide: (doc: IDocument, seq: number, decision: 'approve' | 'reject', comment: string) =>
      run('decide', doc, seq, (site) => decideVersionApi(site, doc.documentId!, seq, decision, comment)),
  };
}